import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import { IconX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { merge } from "lodash";
import { useEffect } from "react";

type Props = {
	data: any;
};

export default function ViewAmmendmentModal({ data }: Props) {
	const modal = useModal();
	const navigate = useNavigate();
	const { closeModal } = useModalControl();

	useEffect(() => {
		if (data) {
			console.log(data);
		}
	}, [data]);

	return (
		<form className="w-full h-full">
			<div className="flex flex-col w-full h-full">
				<div className="flex shrink-0 items-center justify-between h-[50px] px-4 border-b border-slate-200">
					<span className="text-base font-bold text-foreground-dark">Case Ammendment {data.status ? `(${data.status})` : "Motion"}</span>
					<button onClick={closeModal} type="button" className="w-[20px] h-[20px] text-foreground opacity-40 hover:opacity-100">
						<IconX size={20} stroke={1.5} />
					</button>
				</div>

				<div className="flex flex-col w-full h-full">
					<Tabs className="flex h-full" variant="compact" defaultValue="request">
						<TabsList className="pt-4">
							<TabsTrigger value="request">Request</TabsTrigger>
							<TabsTrigger value="current">Current</TabsTrigger>
						</TabsList>

						<TabsContent className="h-[calc(100svh-105px)] p-4" value="request">
							<AmendmentDefendantTable defendants={data.caseAmendment.case_ammendment_defendants} />
							<AmendmentAttachmentTable medias={data.caseAmendment.all_ammendment_media} />
							<AmendmentEvidenceTable evidenceData={data.caseAmendment.case_ammendment_evidence} />
						</TabsContent>

						<TabsContent className="h-[calc(100svh-105px)] p-4" value="current">
							<DefendantTable defendants={data.case.case_defendants} />
							<AttachmentTable medias={data.case.medias} />
							<EvidenceTable evidenceData={data.case.evidence} />
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</form>
	);
}

const AmendmentDefendantTable = ({ defendants }: { defendants: any[] }) => {
	const toBeAddedDefendants = defendants.filter((item) => !item.case_record_user).map((item) => ({ ...item, request_action: "Add" }));

	const toBeDeletedDefendants = defendants.filter((item) => item.case_record_user).map((item) => ({ ...item, request_action: "Delete" }));

	const mergedDefendants = [...toBeAddedDefendants, ...toBeDeletedDefendants];
	const unifiedDefendantColumns: ColumnDef<any>[] = [
		{
			id: "name",
			label: "Defendant Name",
			cell: ({ data }) => {
				if (data.case_record_user) {
					return data.case_record_user.user.full_name;
				}

				const fullName = [data.first_name, data.middle_name, data.last_name].filter(Boolean).join(" ");

				return fullName || "—";
			},
			headerClassName: "w-5/12",
			cellClassName: "truncate",
		},
		{
			id: "address",
			label: "Address",
			cell: ({ data }) => data.address_1 ?? data.case_record_user?.user?.address_1 ?? "—",
			headerClassName: "w-5/12",
			cellClassName: "truncate",
		},
		{
			id: "request_action",
			label: "Request Action",
			cell: ({ data }) => {
				const isAddition = !data.case_record_user;
				return <span className={isAddition ? "text-green-600 font-medium" : "text-red-600 font-medium"}>{isAddition ? "Add" : "Delete"}</span>;
			},
			headerClassName: "w-2/12 text-center",
			cellClassName: "text-center",
		},
	];

	return (
		<div className="flex flex-col w-full gap-2 mb-5">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Defendants</span>
			</div>

			<DataTable
				className="border-collapse w-full table-fixed"
				data={mergedDefendants}
				columns={unifiedDefendantColumns}
				stickyHead={false}
				stickyCol={false}
				showBorder={true}
				compact={true}
				showToolbar={false}
			/>
		</div>
	);
};

const AmendmentAttachmentTable = ({ medias }: { medias: any[] }) => {
	const toBeAddedAttachments = medias.filter((item) => item.collection_name === "temp_case_records").map((item) => ({ ...item, request_action: "Add" }));

	const toBeDeletedAttachments = medias.filter((item) => item.collection_name === "case_records").map((item) => ({ ...item, request_action: "Delete" }));

	const mergedAttachments = [...toBeAddedAttachments, ...toBeDeletedAttachments];

	function formatFileSize(bytes: number) {
		if (!bytes) return "0 B";
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${Number.parseFloat((bytes / 1024 ** i).toFixed(2))} ${sizes[i]}`;
	}

	function formatDate(dateString: string, format = "PPp") {
		return dateString ? new Date(dateString).toLocaleString() : "—";
	}

	const unifiedColumns: ColumnDef<any>[] = [
		{
			id: "name",
			label: "File Name",
			cell: ({ data }) => (
				<button type="button" className="font-semibold text-blue-600 hover:text-blue-500 truncate w-full text-left">
					{data.name || data.file_name || "—"}
				</button>
			),
			headerClassName: "w-3/12",
			cellClassName: "truncate",
		},
		{
			id: "mime_type",
			label: "File Type",
			cell: ({ data }) => data.mime_type ?? "—",
			headerClassName: "w-2/12",
		},
		{
			id: "size",
			label: "File Size",
			cell: ({ data }) => <span>{formatFileSize(data.size)}</span>,
			headerClassName: "w-2/12",
		},
		{
			id: "created_at",
			label: "Date Created",
			cell: ({ data }) => <span>{formatDate(data.created_at, "PPp")}</span>,
			headerClassName: "w-3/12 text-right",
			cellClassName: "text-right",
		},
		{
			id: "request_action",
			label: "Request Action",
			cell: ({ data }) => (
				<span className={data.request_action === "Add" ? "text-green-600 font-medium" : "text-red-600 font-medium"}>{data.request_action}</span>
			),
			headerClassName: "w-2/12 text-center",
			cellClassName: "text-center",
		},
	];

	return (
		<div className="flex flex-col w-full gap-2 mt-10">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Upload Files</span>
			</div>

			<DataTable
				className="border-collapse w-full table-fixed"
				data={mergedAttachments}
				columns={unifiedColumns}
				stickyHead={false}
				stickyCol={false}
				showBorder={true}
				compact={true}
				showToolbar={false}
			/>
		</div>
	);
};

const AmendmentEvidenceTable = ({ evidenceData }: { evidenceData: any[] }) => {
	const toBeAddedEvidence = evidenceData
		.filter((item) => !item.case_evidence_id)
		.map((item) => ({
			...item,
			request_action: "Add",
			file_name: item.evidence?.file_name || "—", // Ensure file_name is included
		}));

	const toBeDeletedEvidence = evidenceData
		.filter((item) => item.case_evidence_id)
		.map((item) => ({
			...item.evidence, // Flatten evidence object
			request_action: "Delete",
			file_name: item.evidence?.file_name || "—", // Ensure file_name is included
		}));

	const unifiedEvidence = [...toBeAddedEvidence, ...toBeDeletedEvidence];
	console.log(unifiedEvidence);
	const evidenceColumns: ColumnDef<any>[] = [
		{
			id: "parties",
			label: "Party Type",
			cell: ({ data }) => data.parties ?? "—",
		},
		{
			id: "evidence_type",
			label: "Evidence Type",
			cell: ({ data }) => data.evidence_type ?? "—",
		},
		{
			id: "exhibit",
			label: "Exhibit",
			cell: ({ data }) => data.exhibit ?? "—",
		},
		{
			id: "request_action",
			label: "Request Action",
			cell: ({ data }) => (
				<span className={data.request_action === "Add" ? "text-green-600 font-medium" : "text-red-600 font-medium"}>{data.request_action}</span>
			),
			headerClassName: "w-2/12 text-center",
			cellClassName: "text-center",
		},
	];

	return (
		<div className="flex flex-col w-full gap-2 mt-10">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Pieces of Evidence</span>
			</div>

			<DataTable
				className="border-collapse"
				data={unifiedEvidence}
				columns={evidenceColumns}
				stickyHead={false}
				stickyCol={false}
				showBorder={true}
				compact={true}
				showToolbar={false}
			/>
		</div>
	);
};

//

const DefendantTable = ({ defendants }: { defendants: any[] }) => {
	const defendantColumns: ColumnDef<any>[] = [
		{
			id: "name",
			label: "Defendant Name",
			cell: ({ data }) => data.user?.full_name ?? "—",
		},
		{
			id: "address",
			label: "Address",
			cell: ({ data }) => data.address ?? "—",
		},
	];

	return (
		<div className="flex flex-col w-full gap-2 mb-10">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Defendants</span>
			</div>

			<DataTable data={defendants || []} columns={defendantColumns} stickyHead={false} stickyCol={false} showBorder={true} compact={true} showToolbar={false} />
		</div>
	);
};

const AttachmentTable = ({ medias }: { medias: any[] }) => {
	function formatFileSize(bytes: number) {
		if (!bytes) return "0 B";
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${Number.parseFloat((bytes / 1024 ** i).toFixed(2))} ${sizes[i]}`;
	}

	function formatDate(dateString: string, format = "PPp") {
		return dateString ? new Date(dateString).toLocaleString() : "—";
	}

	const caseDocumentsColumns: ColumnDef<any>[] = [
		{
			id: "name",
			label: "File Name",
			cell: ({ data }) => (
				<button type="button" className="font-semibold text-blue-600 hover:text-blue-500">
					{data.name || data.file_name || "—"}
				</button>
			),
		},
		{
			id: "mime_type",
			label: "File Type",
			cell: ({ data }) => data.mime_type ?? "—",
		},
		{
			id: "size",
			label: "File Size",
			cell: ({ data }) => <span>{formatFileSize(data.size)}</span>,
		},
		{
			id: "created_at",
			label: "Date Created",
			cell: ({ data }) => <span>{formatDate(data.created_at, "PPp")}</span>,
			cellClassName: "text-right",
			headerClassName: "text-right",
		},
	];

	return (
		<div className="flex flex-col w-full gap-2 mb-10">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Complaint and Annexes</span>
			</div>

			<DataTable
				className="border-collapse"
				data={medias || []}
				columns={caseDocumentsColumns}
				stickyHead={false}
				stickyCol={false}
				showBorder={true}
				compact={true}
				showToolbar={false}
			/>
		</div>
	);
};

const EvidenceTable = ({ evidenceData }: { evidenceData: any[] }) => {
	const filteredEvidence = evidenceData.filter((item) => item.evidence !== null);

	const evidenceColumns: ColumnDef<any>[] = [
		{
			id: "parties",
			label: "Party Type",
			cell: ({ data }) => data.parties ?? "—",
		},
		{
			id: "evidence_type",
			label: "Evidence Type",
			cell: ({ data }) => data.evidence_type ?? "—",
		},
		{
			id: "exhibit",
			label: "Exhibit",
			cell: ({ data }) => data.exhibit ?? "—",
		},
		{
			id: "file_name",
			label: "File Name",
			cell: ({ data }) =>
				data.evidence ? (
					<a href={data.evidence.original_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
						{data.evidence.name || data.evidence.file_name}
					</a>
				) : (
					"—"
				),
		},
	];

	return (
		<div className="flex flex-col w-full gap-2">
			<div className="flex flex-col w-auto">
				<span className="text-base font-bold text-foreground-dark">Pieces of Evidence</span>
			</div>

			<DataTable
				className="border-collapse"
				data={filteredEvidence}
				columns={evidenceColumns}
				stickyHead={false}
				stickyCol={false}
				showBorder={true}
				compact={true}
				showToolbar={false}
			/>
		</div>
	);
};
